import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

type ThicknessType = 'verythin' | 'thin' | 'medium' | 'thick' | 'verythick'
type LineStyleType = 'solid' | 'dotted' | 'dashed' | 'double'
type ShapeType = 'arrow' | 'circle' | 'rectangle' | 'rhombus' | 'flat' | 'crow'
type SizeType = 'small' | 'medium' | 'large'
export type MarkerShapeType = { shape: ShapeType, size: SizeType }

export type ArrowDb = {
  sheetId: string;
  fromId: string;
  toId: string;
  order: number;
  thickness: ThicknessType; //'verythin':'極細', 'thin':'細', 'medium':'通常', 'thick':'太', 'verythick':'極太'
  color: string;
  lineStyle: LineStyleType; //'solid': '実線', 'dotted': '点線', 'dashed':'破線', 'double':'二重線'
  return: boolean;
  shapes: { head: MarkerShapeType, tail: MarkerShapeType };
  //shape --- 'arrow': '三角', 'circle': '丸', 'rectangle': '四角', 'rhombus': '菱形', 'flat': 'フラット', 'crow': 'カラスの足'
  //size ---- 'small':'小', 'medium':'中', 'large':'大'
  labels: {head1:   {label: string}, head2:   {label: string},
           middle1: {label: string}, middle2: {label: string},
           tail1:   {label: string}, tail2:   {label: string}};
  imageId: string;
  links?: string[];
  notes: string;
  goodsId?: string;
  version?: number;
}
const initial: ArrowDb = {
  sheetId: '',
  fromId: '',
  toId: '',
  order: 0,
  thickness: 'medium',
  color: '#ffffff',
  lineStyle: 'solid',
  return: false,
  shapes: { head: { shape: 'arrow', size: 'medium' }, tail: { shape: 'flat', size: 'medium' } },
  labels: { head1: { label: '' }, head2: { label: '' }, middle1: { label: '' }, middle2: { label: '' }, tail1: { label: '' }, tail2: { label: '' } },
  imageId: '',
  links: [],
  notes: '',
  goodsId: undefined,
  version: undefined
};

export type ArrowDbHashType = {
  [key in string]: ArrowDb;
}

export type ArrowAddPayload = {
  id?: string;
} & ArrowDb;
export type ArrowUpdatePayload = {
  id: string;
} & { [P in keyof ArrowDb]?: ArrowDb[P] };

export type ArrowDbState = {
  arrows: ArrowDbHashType;
}
export const generateArrowId = (): string => generateId('a');
let initialState: ArrowDbState = { arrows: {} };
const key = 'arrows';

const arrowDbSlice = createSlice({
  name: 'arrowDb',
  initialState,
  reducers: {
    initializeArrows(state: ArrowDbState) {
      state.arrows = {};
    },
    addArrow(state: ArrowDbState, action: PayloadAction<ArrowAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateArrowId();
      state.arrows[id] = obj;
      saveData(key, id, obj);
    },
    updateArrow(state: ArrowDbState, action: PayloadAction<ArrowUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.arrows[id] === undefined) {
        console.log(`*** Arrow not found: ${id}`);
      } else {
        const newData = { ...state.arrows[id], ...obj };
        state.arrows[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteArrow(state: ArrowDbState, action: PayloadAction<ArrowUpdatePayload>) {
      const id = action.payload.id;
      delete state.arrows[id];
      deleteData(key, id);
    },
    saveArrows(state: ArrowDbState) {
      saveAllData(key, state.arrows);
    },
    signUpArrows(state: ArrowDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.arrows, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: ArrowDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.arrows = updatedValues;
      if (changed) saveAllData(key, state.arrows);
    })
  }
})

export const { initializeArrows, addArrow, updateArrow, deleteArrow, saveArrows, signUpArrows } = arrowDbSlice.actions;
export default arrowDbSlice.reducer;
